import { Venues, VendorsApi } from '@weddingspot/ws-api-client';
import cn from 'classnames';
import copy from 'copy-to-clipboard';
import * as React from 'react';
import { isMobile } from 'react-device-detect';
import * as types from '../../types';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import { hbToastInfo } from '../toast';
import { useState } from 'react';
import { CventImage, FeatureFlagContext, FLAG_SHOPPING_CART_FOR_SRP, WSAppContext } from '@weddingspot/ws-core-components';
const { default: LazyLoad } = require('react-lazyload');

const TextTruncate = require('react-text-truncate');

const _styles = require('./curatedListItem.scss');

export interface VenueCardProps {
    venue: types.VenueDataItem;
    classes?: {
        wrapper?: string;
        title?: string;
    };
    loggedIn?: boolean;
    index: number;
    market?: string;
    region?: string;
    style?: string;
    isFavorite: boolean;
    showSignInModal: () => void;
    handleContactCTAClick: (index: number) => void;
}

const CuratedListItem = (props: VenueCardProps) => {
    const [isFavorite, setIsFavorite] = useState(props.isFavorite);
    const [isFavoriteUpdating, setIsFavoriteUpdating] = useState(false);
    const { userIsAuthenticated } = React.useContext(WSAppContext);
    const { flagEnabled, isInitialized: isFlagsInitialized } = React.useContext(FeatureFlagContext);

    const logAmplitudeEvent = (eventName: AnalyticsEvent) => {
        const params: object = {
            'Shopping Cart Shown': isFlagsInitialized && flagEnabled(FLAG_SHOPPING_CART_FOR_SRP),
            Market: props.market,
            Region: props.region,
            Style: props.style,
            VenueId: props.venue.id,
            Source: 'Curated Lists Pages',
        };
        logEvent(eventName, params);
    };

    const updateFavorite = React.useCallback(
        (e: React.SyntheticEvent) => {
            logAmplitudeEvent(AnalyticsEvent.EVTNM_CLICKED_FAVORITES);
            if (!userIsAuthenticated) {
                props.showSignInModal();
            } else if (!isFavoriteUpdating) {
                setIsFavoriteUpdating(true);
                if (!isFavorite) {
                    VendorsApi.setFavorite([props.venue.id], Venues.FavoriteType.WEDDING).then((_) => {
                        setIsFavorite(true);
                        setIsFavoriteUpdating(false);
                    });
                } else {
                    VendorsApi.removeFavorite([props.venue.id]).then((_) => {
                        setIsFavorite(false);
                        setIsFavoriteUpdating(false);
                    });
                }
            }
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        },
        [userIsAuthenticated]
    );

    const handleOnCardClick = (venue: types.VenueDataItem) => {
        logAmplitudeEvent(AnalyticsEvent.EVTNM_VISITED_WEDDING_VENUE_PROFILE);
        window.open(venue.profile_url, !isMobile ? '_blank' : '_self');
    };

    const shareNative = (venue: types.VenueDataItem, navigator: Navigator) => {
        return new Promise(async (resolve) => {
            await navigator.share({
                title: venue.name,
                text: venue.description,
                url: `${location.origin}${venue.profile_url}`,
            });
            /* @ts-ignore */
            resolve();
        });
    };

    const shareDesktop = (venue: types.VenueDataItem) => {
        copy(`${location.origin}${venue.profile_url}`);
        hbToastInfo('Link copied to clipboard.');
    };

    async function handleShare(venue: types.VenueDataItem) {
        let navigator: Navigator = window.navigator;
        /* @ts-ignore */
        if (navigator && navigator.share) {
            await shareNative(venue, navigator);
        } else {
            await shareDesktop(venue);
        }
    }

    const handleContactVenueClick = (index: number) => {
        logAmplitudeEvent(AnalyticsEvent.EVTNM_CLICKED_CONTACT_CTA);
        props.handleContactCTAClick(index);
    };

    let titleTextSizeClass;
    const venueNameLength = props.venue.name.split(' ').length;
    if (venueNameLength > 6) {
        titleTextSizeClass = 'title-sm';
    } else if (venueNameLength < 7 && venueNameLength > 2) {
        titleTextSizeClass = 'title-md';
    } else {
        titleTextSizeClass = 'title-lg';
    }
    const isFavIconCLass = isFavorite ? 'icon-hb-nx-heart-filled' : 'icon-hb-nx-heart';
    const sliderSettings = {
        dots: true,
        appendDots: (dots: React.ReactNode): JSX.Element => {
            return (
                <div className={_styles.sliderDots}>
                    <ul> {dots} </ul>
                </div>
            );
        },
        nextArrow: '>',
        prevArrow: '<',
    };

    return (
        <div className={cn(_styles.wrapper)}>
            <div className={_styles.header}>
                <div className={_styles.titleLeft}>
                    <div className={_styles.titleRank}>{props.index + 1}</div>
                    <div className={cn(_styles.titleText, _styles[titleTextSizeClass])}>
                        <h3 className={cn(_styles.venueNameText)}>{props.venue.name}</h3>
                        <span className={_styles.titlePlace}>
                            {props.venue.city}, {props.venue.market}
                        </span>
                    </div>
                </div>
                <div className={_styles.titleRight}>
                    <a
                        className={_styles.titleLink}
                        onClick={(e) => {
                            e.preventDefault();
                            handleShare(props.venue);
                        }}
                    >
                        <span className={'icon icon-hb-share'} />
                        Share
                    </a>
                    <a className={_styles.titleLink} onClick={updateFavorite}>
                        <span className={cn('icon', isFavIconCLass)} />
                        Favorite
                    </a>
                </div>
            </div>
            <div
                onClick={(e) => {
                    e.preventDefault();
                    handleOnCardClick(props.venue);
                }}
            >
                <LazyLoad offset={100} once={true}>
                    <div className={_styles.imageContainer}>
                        <CventImage
                            alt={`${props.venue.name} wedding ${props.venue.region}, ${props.venue.market}`}
                            src={props.venue.all_photos[0]}
                        />
                    </div>
                </LazyLoad>

                <div className={_styles.description}>
                    <TextTruncate line={3} element='span' truncateText='…' text={props.venue.description} />
                </div>
            </div>
            <div className={_styles.footer}>
                <div className={_styles.footerLeft}>
                    <p>{props.venue.budget_string}</p>
                    <a
                        onClick={(e) => {
                            e.preventDefault();
                            handleOnCardClick(props.venue);
                        }}
                    >
                        See More Photos
                    </a>
                </div>

                <div className={_styles.contactCTA}>
                    <button className={'nx-button nx-button--primary'} onClick={() => handleContactVenueClick(props.index)}>
                        CONTACT
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CuratedListItem;
